import { useState, useEffect } from 'react';
import { Component, ComponentType } from '../../../types/elearning';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import Button from '../../ui/Button';
import { wordpressAPI } from '../../../services/wordpressApi';
import { WPMedia } from '../../../types/wordpress';
import ComponentPalette from './ComponentPalette';
import ComponentRenderer from '../../elearning/content/ComponentRenderer';
import { getDefaultContent } from './SlideBuilder';
import { stripHtml } from '../../../utils/htmlEntities';
import RichTextEditor from '../../admin/RichTextEditor';

interface ComponentEditorProps {
  component: Component;
  onUpdate: (component: Component) => void;
  availableSlideComponents?: Component[]; // For drag-and-drop from slide
}

export default function ComponentEditor({ component, onUpdate, availableSlideComponents = [] }: ComponentEditorProps) {
  const [content, setContent] = useState(component.content);
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaResults, setMediaResults] = useState<WPMedia[]>([]);
  const [, setSearchingMedia] = useState(false);

  useEffect(() => {
    setContent(component.content);
  }, [component]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if ((component.type === 'image' || component.type === 'card' || component.type === 'resource') && mediaSearch.trim()) {
        searchMedia();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [mediaSearch, component.type]);

  const searchMedia = async () => {
    try {
      setSearchingMedia(true);
      const results = await wordpressAPI.fetchMediaList({ 
        search: mediaSearch,
        per_page: 10 
      });
      setMediaResults(results);
    } catch (err) {
      console.error('Failed to search media:', err);
      setMediaResults([]);
    } finally {
      setSearchingMedia(false);
    }
  };

  const handleContentChange = (updates: any) => {
    const newContent = { ...content, ...updates };
    setContent(newContent);
    onUpdate({
      ...component,
      content: newContent,
    });
  };

  switch (component.type) {
    case 'text':
      return (
        <RichTextEditor
          key={component.id}
          label="Text Content"
          placeholder="Enter text content..."
          value={(content as any).html || ''}
          onChange={(html) => handleContentChange({ html })}
        />
      );

    case 'heading':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Heading Level
            </label>
            <Select
              value={(content as any).level?.toString() || '2'}
              onChange={(e) =>
                handleContentChange({ level: parseInt(e.target.value) as any })
              }
              options={[
                { value: '1', label: 'H1 - Largest' },
                { value: '2', label: 'H2 - Large' },
                { value: '3', label: 'H3 - Medium' },
                { value: '4', label: 'H4 - Small' },
                { value: '5', label: 'H5 - Smaller' },
                { value: '6', label: 'H6 - Smallest' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Heading Text
            </label>
            <Input
              type="text"
              value={(content as any).text || ''}
              onChange={(e) => handleContentChange({ text: e.target.value })}
              placeholder="Enter heading text..."
            />
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search WordPress Media
            </label>
            <Input
              type="text"
              value={mediaSearch}
              onChange={(e) => setMediaSearch(e.target.value)}
              placeholder="Search for images..."
            />
          </div>

          {mediaResults.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Image
              </label>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {mediaResults.map((media) => (
                  <button
                    key={media.id}
                    onClick={() =>
                      handleContentChange({
                        mediaId: media.id,
                        alt: stripHtml(media.title.rendered) || '',
                      })
                    }
                    className={`p-2 border rounded-lg hover:border-primary transition-colors ${
                      (content as any).mediaId === media.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={media.source_url}
                      alt={stripHtml(media.title.rendered)}
                      className="w-full h-20 object-cover rounded"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {(content as any).mediaId > 0 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alt Text
                </label>
                <Input
                  type="text"
                  value={(content as any).alt || ''}
                  onChange={(e) => handleContentChange({ alt: e.target.value })}
                  placeholder="Describe the image..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alignment
                  </label>
                  <Select
                    value={(content as any).alignment || 'center'}
                    onChange={(e) => handleContentChange({ alignment: e.target.value })}
                    options={[
                      { value: 'left', label: 'Left' },
                      { value: 'center', label: 'Center' },
                      { value: 'right', label: 'Right' },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Size
                  </label>
                  <Select
                    value={(content as any).size || 'medium'}
                    onChange={(e) => handleContentChange({ size: e.target.value })}
                    options={[
                      { value: 'small', label: 'Small' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'large', label: 'Large' },
                      { value: 'full', label: 'Full Width' },
                    ]}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      );

    case 'video':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              YouTube URL or Video ID
            </label>
            <Input
              type="text"
              value={(content as any).youtubeId || ''}
              onChange={(e) => handleContentChange({ youtubeId: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=... or video ID"
            />
            <p className="text-xs text-gray-500 mt-1">
              Paste a YouTube URL or just the video ID
            </p>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={(content as any).autoplay || false}
                onChange={(e) => handleContentChange({ autoplay: e.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-700">Autoplay video</span>
            </label>
          </div>
        </div>
      );

    case 'callout':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <Select
              value={(content as any).type || 'info'}
              onChange={(e) => handleContentChange({ type: e.target.value })}
              options={[
                { value: 'info', label: 'Info' },
                { value: 'warning', label: 'Warning' },
                { value: 'error', label: 'Error' },
                { value: 'success', label: 'Success' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title (Optional)
            </label>
            <Input
              type="text"
              value={(content as any).title || ''}
              onChange={(e) => handleContentChange({ title: e.target.value })}
              placeholder="Alert title..."
            />
          </div>
          <div>
            <RichTextEditor
              key={`${component.id}-callout`}
              label="Content"
              placeholder="Enter content..."
              value={(content as any).content || ''}
              onChange={(html) => handleContentChange({ content: html })}
            />
          </div>
        </div>
      );

    case 'card':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Title *
            </label>
            <Input
              type="text"
              value={(content as any).title || ''}
              onChange={(e) => handleContentChange({ title: e.target.value })}
              placeholder="Card title..."
            />
          </div>
          <div>
            <RichTextEditor
              key={`${component.id}-card`}
              label="Card Content"
              placeholder="Card content..."
              value={(content as any).text || ''}
              onChange={(html) => handleContentChange({ text: html })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search WordPress Media (Optional Image)
            </label>
            <Input
              type="text"
              value={mediaSearch}
              onChange={(e) => setMediaSearch(e.target.value)}
              placeholder="Search for images..."
            />
          </div>
          {mediaResults.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Image
              </label>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {mediaResults.map((media) => (
                  <button
                    key={media.id}
                    onClick={() => handleContentChange({ imageId: media.id })}
                    className={`p-2 border rounded-lg hover:border-primary transition-colors ${
                      (content as any).imageId === media.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={media.source_url}
                      alt={stripHtml(media.title.rendered)}
                      className="w-full h-20 object-cover rounded"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link URL (Optional)
            </label>
            <Input
              type="url"
              value={(content as any).link || ''}
              onChange={(e) => handleContentChange({ link: e.target.value })}
              placeholder="https://..."
            />
          </div>
          {(content as any).link && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link Text
              </label>
              <Input
                type="text"
                value={(content as any).linkText || 'Learn More'}
                onChange={(e) => handleContentChange({ linkText: e.target.value })}
                placeholder="Button text..."
              />
            </div>
          )}
        </div>
      );

    case 'resource':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search WordPress Media *
            </label>
            <Input
              type="text"
              value={mediaSearch}
              onChange={(e) => setMediaSearch(e.target.value)}
              placeholder="Search for files..."
            />
          </div>
          {mediaResults.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {mediaResults.map((media) => (
                  <button
                    key={media.id}
                    onClick={() =>
                      handleContentChange({
                        mediaId: media.id,
                        fileName: stripHtml(media.title.rendered) || media.slug,
                      })
                    }
                    className={`w-full text-left p-3 border rounded-lg hover:border-primary transition-colors ${
                      (content as any).mediaId === media.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium text-sm">{stripHtml(media.title.rendered) || media.slug}</p>
                    <p className="text-xs text-gray-500">{media.mime_type}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(content as any).mediaId > 0 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File Name *
                </label>
                <Input
                  type="text"
                  value={(content as any).fileName || ''}
                  onChange={(e) => handleContentChange({ fileName: e.target.value })}
                  placeholder="File name..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={(content as any).description || ''}
                  onChange={(e) => handleContentChange({ description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
                  placeholder="File description..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Download Button Text *
                </label>
                <Input
                  type="text"
                  value={(content as any).downloadText || 'Download'}
                  onChange={(e) => handleContentChange({ downloadText: e.target.value })}
                  placeholder="Download"
                />
              </div>
            </>
          )}
        </div>
      );

    case 'table':
      const tableContent = (content as any) || { hasHeader: false, rows: [] };
      const tableRows = tableContent.rows || [];

      return (
        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={tableContent.hasHeader || false}
                onChange={(e) =>
                  handleContentChange({ hasHeader: e.target.checked })
                }
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-700">First row is header</span>
            </label>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Rows
              </label>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  const newRows = [
                    ...tableRows,
                    { cells: [{ content: '' }, { content: '' }] },
                  ];
                  handleContentChange({ rows: newRows });
                }}
              >
                Add Row
              </Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
              {tableRows.map((row: any, rowIndex: number) => (
                <div key={rowIndex} className="flex gap-2 items-center">
                  {row.cells.map((cell: any, cellIndex: number) => (
                    <input
                      key={cellIndex}
                      type="text"
                      value={cell.content || ''}
                      onChange={(e) => {
                        const newRows = [...tableRows];
                        newRows[rowIndex].cells[cellIndex].content = e.target.value;
                        handleContentChange({ rows: newRows });
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder={`Cell ${cellIndex + 1}`}
                    />
                  ))}
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      const newRows = tableRows.map((r: any) => ({
                        ...r,
                        cells: [...r.cells, { content: '' }],
                      }));
                      handleContentChange({ rows: newRows });
                    }}
                  >
                    +
                  </Button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      const newRows = tableRows.filter((_: any, i: number) => i !== rowIndex);
                      handleContentChange({ rows: newRows });
                    }}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      );

    case 'accordion':
      const accordionContent = (content as any) || { items: [] };
      const accordionItems = accordionContent.items || [];

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Accordion Items
            </label>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                const newItems = [
                  ...accordionItems,
                  { title: '', content: '', defaultExpanded: false },
                ];
                handleContentChange({ items: newItems });
              }}
            >
              Add Item
            </Button>
          </div>
          <div className="space-y-3">
            {accordionItems.map((item: any, index: number) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <div className="space-y-2">
                  <Input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => {
                      const newItems = [...accordionItems];
                      newItems[index].title = e.target.value;
                      handleContentChange({ items: newItems });
                    }}
                    placeholder="Item title..."
                  />
                  <RichTextEditor
                    key={`${component.id}-accordion-${index}`}
                    label=""
                    placeholder="Item content..."
                    value={item.content || ''}
                    onChange={(html) => {
                      const newItems = [...accordionItems];
                      newItems[index].content = html;
                      handleContentChange({ items: newItems });
                    }}
                  />
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={item.defaultExpanded || false}
                      onChange={(e) => {
                        const newItems = [...accordionItems];
                        newItems[index].defaultExpanded = e.target.checked;
                        handleContentChange({ items: newItems });
                      }}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-gray-600">Expanded by default</span>
                  </label>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      const newItems = accordionItems.filter((_: any, i: number) => i !== index);
                      handleContentChange({ items: newItems });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'columns':
      const columnsContent = (content as any) || { columnCount: 2, columns: [] };
      const columnCount = columnsContent.columnCount || 2;
      const columns = columnsContent.columns || Array(columnCount).fill(null).map(() => ({ components: [] }));
      const [showColumnPalette, setShowColumnPalette] = useState<number | null>(null);
      const [draggedComponent, setDraggedComponent] = useState<Component | null>(null);

      const handleAddComponentToColumn = (columnIndex: number, type: ComponentType) => {
        const newColumns = [...columns];
        if (!newColumns[columnIndex]) {
          newColumns[columnIndex] = { components: [] };
        }
        const newComponent: Component = {
          id: `comp-${Date.now()}-${columnIndex}`,
          type,
          order: (newColumns[columnIndex].components?.length || 0) + 1,
          content: getDefaultContent(type),
        };
        newColumns[columnIndex] = {
          ...newColumns[columnIndex],
          components: [...(newColumns[columnIndex].components || []), newComponent],
        };
        handleContentChange({ columnCount, columns: newColumns });
        setShowColumnPalette(null);
      };

      const handleRemoveComponentFromColumn = (columnIndex: number, componentId: string) => {
        const newColumns = [...columns];
        if (newColumns[columnIndex]?.components) {
          newColumns[columnIndex] = {
            ...newColumns[columnIndex],
            components: newColumns[columnIndex].components.filter((c: any) => c.id !== componentId),
          };
          handleContentChange({ columnCount, columns: newColumns });
        }
      };

      const handleDragStart = (e: React.DragEvent, comp: Component) => {
        setDraggedComponent(comp);
        e.dataTransfer.effectAllowed = 'move';
      };

      const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      };

      const handleDrop = (e: React.DragEvent, columnIndex: number) => {
        e.preventDefault();
        if (!draggedComponent) return;

        const newColumns = [...columns];
        if (!newColumns[columnIndex]) {
          newColumns[columnIndex] = { components: [] };
        }

        // Create a copy of the component for the column
        const componentCopy: Component = {
          ...draggedComponent,
          id: `comp-${Date.now()}-${columnIndex}`,
          order: (newColumns[columnIndex].components?.length || 0) + 1,
        };

        newColumns[columnIndex] = {
          ...newColumns[columnIndex],
          components: [...(newColumns[columnIndex].components || []), componentCopy],
        };

        handleContentChange({ columnCount, columns: newColumns });
        setDraggedComponent(null);
      };

      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Column Count *
            </label>
            <Select
              value={columnCount.toString()}
              onChange={(e) => {
                const count = parseInt(e.target.value) as 2 | 3;
                const newColumns = Array(count)
                  .fill(null)
                  .map((_, i) => columns[i] || { components: [] });
                handleContentChange({ columnCount: count, columns: newColumns });
              }}
              options={[
                { value: '2', label: '2 Columns' },
                { value: '3', label: '3 Columns' },
              ]}
            />
          </div>

          {/* Available Slide Components for Drag */}
          {availableSlideComponents.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-blue-900 mb-2">Drag components from slide:</p>
              <div className="flex flex-wrap gap-2">
                {availableSlideComponents.map((comp) => (
                  <div
                    key={comp.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, comp)}
                    className="px-2 py-1 bg-white border border-blue-300 rounded text-xs cursor-move hover:bg-blue-100 capitalize"
                  >
                    {comp.type.replace('-', ' ')}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Column Content
            </label>
            <div className={`grid grid-cols-1 md:grid-cols-${columnCount} gap-4`}>
              {Array(columnCount)
                .fill(null)
                .map((_, columnIndex) => (
                  <div
                    key={columnIndex}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, columnIndex)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Column {columnIndex + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => setShowColumnPalette(showColumnPalette === columnIndex ? null : columnIndex)}
                        className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100"
                        title="Add component"
                      >
                        + Add
                      </button>
                    </div>

                    {showColumnPalette === columnIndex && (
                      <div className="mb-3 p-2 bg-white border border-gray-300 rounded">
                        <ComponentPalette
                          onSelectComponent={(type) => handleAddComponentToColumn(columnIndex, type)}
                        />
                      </div>
                    )}

                    <div
                      className="space-y-2 min-h-[100px]"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, columnIndex)}
                    >
                      {columns[columnIndex]?.components?.length > 0 ? (
                        columns[columnIndex].components.map((comp: Component) => (
                          <div
                            key={comp.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, comp)}
                            className="bg-white border border-gray-200 rounded p-2 text-sm cursor-move hover:border-primary"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500 capitalize font-medium">
                                {comp.type.replace('-', ' ')}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveComponentFromColumn(columnIndex, comp.id)}
                                className="text-red-600 hover:text-red-700 text-xs font-bold"
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                            <div className="text-xs text-gray-600 max-h-32 overflow-y-auto">
                              <ComponentRenderer component={comp} />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center">
                          <p className="text-xs text-gray-400 mb-2">
                            Drop components here
                          </p>
                          <p className="text-xs text-gray-400">
                            or click "+ Add" to create new
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      );

    case 'knowledge-check':
      const kcContent = (content as any) || {
        question: '',
        type: 'multiple-choice',
        points: 1,
      };

      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question *
            </label>
            <textarea
              value={kcContent.question || ''}
              onChange={(e) => handleContentChange({ question: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
              placeholder="Enter question..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question Type *
            </label>
            <Select
              value={kcContent.type || 'multiple-choice'}
              onChange={(e) => {
                const updates: any = { type: e.target.value };
                if (e.target.value === 'multiple-choice') {
                  updates.options = [
                    { text: '', isCorrect: false },
                    { text: '', isCorrect: false },
                  ];
                }
                handleContentChange(updates);
              }}
              options={[
                { value: 'multiple-choice', label: 'Multiple Choice' },
                { value: 'true-false', label: 'True/False' },
                { value: 'short-answer', label: 'Short Answer' },
              ]}
            />
          </div>
          {kcContent.type === 'multiple-choice' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Options *
              </label>
              {(kcContent.options || []).map((option: any, index: number) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="text"
                    value={option.text || ''}
                    onChange={(e) => {
                      const newOptions = [...(kcContent.options || [])];
                      newOptions[index].text = e.target.value;
                      handleContentChange({ options: newOptions });
                    }}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1"
                  />
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={option.isCorrect || false}
                      onChange={(e) => {
                        const newOptions = [...(kcContent.options || [])];
                        newOptions[index].isCorrect = e.target.checked;
                        handleContentChange({ options: newOptions });
                      }}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    Correct
                  </label>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      const newOptions = (kcContent.options || []).filter(
                        (_: any, i: number) => i !== index
                      );
                      handleContentChange({ options: newOptions });
                    }}
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  const newOptions = [
                    ...(kcContent.options || []),
                    { text: '', isCorrect: false },
                  ];
                  handleContentChange({ options: newOptions });
                }}
              >
                Add Option
              </Button>
            </div>
          )}
          {kcContent.type === 'true-false' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correct Answer *
              </label>
              <Select
                value={kcContent.correctAnswer?.toString() || ''}
                onChange={(e) =>
                  handleContentChange({ correctAnswer: e.target.value === 'true' })
                }
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
              />
            </div>
          )}
          {kcContent.type === 'short-answer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Answer Key / Keywords
              </label>
              <Input
                type="text"
                value={kcContent.answerKey || ''}
                onChange={(e) => handleContentChange({ answerKey: e.target.value })}
                placeholder="Keywords (comma-separated)"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Points *
            </label>
            <Input
              type="number"
              value={kcContent.points || 1}
              onChange={(e) =>
                handleContentChange({ points: parseInt(e.target.value) || 1 })
              }
              min={1}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Explanation (Optional)
            </label>
            <textarea
              value={kcContent.explanation || ''}
              onChange={(e) => handleContentChange({ explanation: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
              placeholder="Explanation shown after answer..."
            />
          </div>
        </div>
      );

    default:
      return (
        <div className="text-center py-4 text-gray-500">
          Component editor not yet implemented for type: {component.type}
        </div>
      );
  }
}

